import React, { PureComponent } from 'react';
import { Flex } from 'app/components/common/Flex';
import { CheckCircleFilled, MoreOutlined } from '@ant-design/icons';
import './SolutionSummaryPanel.less';
import TimeUtils from '../../../common/util/TimeUtils';
import AppContext from '../../../common/context/AppContext';
import { DetailedSolution, Solution } from '../../../model/SOL49';
import Utils from '../../../common/util/Utils';
import { MeasureDto } from '../../../model/CORE';
import { Badge } from 'antd';
import { PopoverMenu } from '../../common/PopoverMenu';
import MeasureUtils from '../../../common/util/MeasureUtils';
import { ItemType } from 'antd/lib/menu/hooks/useItems';

interface SolutionSummaryPanelProps {
    solution: DetailedSolution;
    selectedSolution: Solution;
    selected: boolean;
    onSelectionChange?(selected: boolean): void;
    nbFlightsConcerned?: number;
    onAddToSolution?: (measures: Array<MeasureDto>) => void;
}

export class SolutionSummaryPanel extends PureComponent<SolutionSummaryPanelProps> {
    handleSelectionChange = () => {
        // If selected is undefined, it means that the component cannot be selected
        this.props.selected !== undefined && this.props.onSelectionChange?.(!this.props.selected);
    };

    render() {
        const className =
            'solution-summary on-hover' +
            (this.props.selected ? ' selected' : '') +
            (!Utils.isExists(this.props.solution.impactSummary) ? ' not-selectable' : '');
        const selectorClassName = this.props.selected !== undefined ? 'show-on-hover' : 'hide';
        return (
            <Badge count={this.props.nbFlightsConcerned} style={{ transform: 'translate(0%, 0%)' }}>
                <Flex className={className} direction="vertical" grow={false} onClick={this.handleSelectionChange}>
                    <Flex style={{ fontWeight: 'bold' }}>
                        <Flex
                            className={'check-circle ' + selectorClassName}
                            size="2em"
                            style={{ padding: '0 0.25em' }}>
                            <CheckCircleFilled />
                        </Flex>
                        <Flex direction="vertical">
                            <Flex>{this.props.solution.name}</Flex>
                            {this.props.solution.tvMatchingKind ? (
                                <Flex style={{ fontSize: '0.85em' }}>{this.props.solution.tvMatchingKind}</Flex>
                            ) : undefined}
                        </Flex>
                        <Flex size="1em">
                            <div className={'more-button'} onClick={e => e.stopPropagation()}>
                                <SolutionSummaryMoreMenu
                                    solution={this.props.solution}
                                    selectedSolution={this.props.selectedSolution}
                                    onAddToSolution={this.props.onAddToSolution}
                                />
                            </div>
                        </Flex>
                    </Flex>
                    <SolutionSummary solution={this.props.solution} />
                </Flex>
            </Badge>
        );
    }
}

interface SolutionSummaryProps {
    solution: DetailedSolution;
}

export class SolutionSummary extends PureComponent<SolutionSummaryProps> {
    render() {
        const impactSummary = this.props.solution.impactSummary;
        if (!Utils.isExists(impactSummary)) return null;

        return (
            <Flex style={{ margin: '1em', color: '#B7B7B7' }}>
                <Flex size="40%" direction="vertical" style={{ rowGap: '0.5em', borderRight: '1px solid white' }}>
                    <Flex style={{ padding: '0 0.5em', fontWeight: 'bold' }}>FLIGHTS</Flex>
                    <Flex>
                        <Flex
                            direction="vertical"
                            style={{ rowGap: '0.5em', borderRight: '1px solid white', padding: '0 0.5em' }}>
                            <Flex direction="vertical" style={{ rowGap: '0.25em' }}>
                                <Flex
                                    style={{
                                        fontSize: '1.5em',
                                        fontWeight: 'bold',
                                        lineHeight: '1em',
                                        justifyContent: 'center',
                                    }}>
                                    {impactSummary?.nbFlightsDelayed}
                                </Flex>
                                <Flex style={{ color: '#FAAD16', justifyContent: 'center' }}>
                                    {impactSummary?.nbFlightsRegulated}
                                </Flex>
                            </Flex>
                            <Flex style={{ justifyContent: 'center' }}>delayed</Flex>
                        </Flex>
                        <Flex direction="vertical" style={{ rowGap: '0.5em', padding: '0 0.5em' }}>
                            <Flex direction="vertical" style={{ rowGap: '0.25em' }}>
                                <Flex
                                    style={{
                                        fontSize: '1.5em',
                                        fontWeight: 'bold',
                                        lineHeight: '1em',
                                        justifyContent: 'center',
                                    }}>
                                    {impactSummary?.nbFlightsRerouted}
                                </Flex>
                                <Flex style={{ color: '#FAAD16', justifyContent: 'center' }}>
                                    {impactSummary?.nbFlightsConcerned}
                                </Flex>
                            </Flex>
                            <Flex style={{ justifyContent: 'center' }}>rerouted</Flex>
                        </Flex>
                    </Flex>
                </Flex>
                <Flex size="40%" direction="vertical" style={{ rowGap: '0.5em', borderRight: '1px solid white' }}>
                    <Flex style={{ padding: '0 0.5em', fontWeight: 'bold' }}>IMPACT</Flex>
                    <Flex>
                        <Flex
                            direction="vertical"
                            style={{ rowGap: '0.5em', borderRight: '1px solid white', padding: '0 0.5em' }}>
                            <Flex direction="vertical" style={{ rowGap: '0.25em' }}>
                                <Flex
                                    style={{
                                        fontSize: '1.5em',
                                        fontWeight: 'bold',
                                        lineHeight: '1em',
                                        justifyContent: 'center',
                                    }}>
                                    {(impactSummary?.solutionCost / 1000).toFixed(1)}
                                </Flex>
                                <Flex style={{ color: '#FAAD16', justifyContent: 'center' }}>
                                    {(impactSummary?.networkCost / 1000).toFixed(1)}
                                </Flex>
                            </Flex>
                            <Flex style={{ justifyContent: 'center' }}>cost</Flex>
                        </Flex>
                        <Flex direction="vertical" style={{ rowGap: '0.5em', padding: '0 0.5em' }}>
                            <Flex direction="vertical" style={{ rowGap: '0.25em' }}>
                                <Flex
                                    style={{
                                        fontSize: '1.5em',
                                        fontWeight: 'bold',
                                        lineHeight: '1em',
                                        justifyContent: 'center',
                                    }}>
                                    {impactSummary &&
                                        (impactSummary.solutionDelayInMs / TimeUtils.MIN_TO_MS).toFixed(0)}
                                </Flex>
                                <Flex style={{ color: '#FAAD16', justifyContent: 'center' }}>
                                    {impactSummary && (impactSummary.networkDelayInMs / TimeUtils.MIN_TO_MS).toFixed(0)}
                                </Flex>
                            </Flex>
                            <Flex style={{ justifyContent: 'center' }}>delay</Flex>
                        </Flex>
                    </Flex>
                </Flex>
                <Flex direction="vertical" style={{ rowGap: '0.5em' }}>
                    <Flex>&nbsp;</Flex>
                    <Flex direction="vertical" style={{ rowGap: '0.5em', padding: '0 0.5em' }}>
                        <Flex style={{ fontSize: '1.5em', fontWeight: 'bold', lineHeight: '1.75em' }}>
                            {Utils.isExists(impactSummary.validity)
                                ? impactSummary &&
                                  (
                                      TimeUtils.diffInMs(impactSummary.validity, AppContext.getCurrentTime()) /
                                      TimeUtils.MIN_TO_MS
                                  ).toFixed(0)
                                : '∞'}
                        </Flex>
                        <Flex>min</Flex>
                    </Flex>
                </Flex>
            </Flex>
        );
    }
}

interface SolutionSummaryMoreMenuProps {
    solution: Solution;
    selectedSolution: Solution;
    onAddToSolution?: (measures: Array<MeasureDto>) => void;
}

class SolutionSummaryMoreMenu extends PureComponent<SolutionSummaryMoreMenuProps> {
    getMenuItems = (): ItemType[] => {
        const hasSelectedSolution = Utils.isExists(this.props.selectedSolution);
        const isCurrentSolutionSelected = this.props.solution === this.props.selectedSolution;
        const addToSelectedSolutionItem = {
            key: 'add-to-current-solution',
            label: 'Add to selected solution',
            disabled: !hasSelectedSolution || isCurrentSolutionSelected,
            children: [
                ...this.props.solution?.measures?.map((measure, idx) => ({
                    key: `add-${measure.measureId.id}-${idx}`,
                    label: `${measure.measureId.id} (${MeasureUtils.toMeasureTypeString(measure)})`,
                    onClick: () => this.props.onAddToSolution?.([measure]),
                })),
                { key: 'divider-1', type: 'divider' },
                {
                    key: 'add-all',
                    label: 'All',
                    onClick: () => this.props.onAddToSolution?.(this.props.solution?.measures),
                },
            ],
        } as ItemType;
        return [addToSelectedSolutionItem];
    };
    render() {
        return (
            <PopoverMenu menuItems={this.getMenuItems}>
                <MoreOutlined />
            </PopoverMenu>
        );
    }
}
